import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Award, Heart, Users, TrendingUp } from "lucide-react";
import SEO from "@/components/SEO";

const AboutPage = ({ seoSettings }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={`About - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="about-page-title">
            {settings?.about_title || "About Us"}
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Discover our story and what makes us different
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" data-testid="about-content">
            <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
              {settings?.about_content || "We are a professional mobile bartending service dedicated to making your events unforgettable."}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-[#F7F4F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="values-section-title">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md" data-testid="value-quality">
              <Award className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality</h3>
              <p className="text-sm text-gray-600">
                We use only premium spirits and fresh ingredients
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md" data-testid="value-passion">
              <Heart className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Passion</h3>
              <p className="text-sm text-gray-600">
                Every cocktail is crafted with love and expertise
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md" data-testid="value-service">
              <Users className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Service</h3>
              <p className="text-sm text-gray-600">
                Professional and friendly service for every guest
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md" data-testid="value-innovation">
              <TrendingUp className="mx-auto mb-4 text-[#8B7355]" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Innovation</h3>
              <p className="text-sm text-gray-600">
                Creative cocktails that wow your guests
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="team-section-title">
              Our Expert Team
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Licensed and certified bartenders with years of experience
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center" data-testid={`team-member-${i}`}>
                <div className="w-48 h-48 mx-auto bg-gradient-to-br from-[#D4C4B0] to-[#8B7355] rounded-full mb-4 flex items-center justify-center">
                  <div className="text-6xl">👨‍🍳</div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Bartender</h3>
                <p className="text-sm text-gray-600">Professional & Certified</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
